import { api, type Me } from './api.ts';
import { html, mount } from './html.ts';
import { ENTITY, formatDateTime, ROLE, t, tk } from './i18n.ts';
import { applyLocale, englishApplied, guestLocale, rememberLocale } from './i18n-en.ts';
import { match, navigate, route } from './router.ts';
import { brand, state } from './state.ts';
import { loading, toastError } from './ui.ts';
import { renderAuth } from './views/auth.ts';
import { renderInvite } from './views/invite.ts';
import { renderReset } from './views/reset.ts';
import { renderLegal } from './views/legal.ts';
import { loadSite, releaseNotice, siteFooter, wireChrome } from './components/chrome.ts';
import { calendarView } from './views/calendar.ts';
import { dashboardView } from './views/dashboard.ts';
import { projectView } from './views/project.ts';
import { projectsView } from './views/projects.ts';
import { companiesView, contactsView } from './views/relations.ts';
import { activityView, settingsView } from './views/settings.ts';

route('/', 'dashboard', dashboardView);
route('/projects', 'projects', projectsView);
route('/projects/:id', 'projects', projectView);
route('/calendar', 'calendar', calendarView);
route('/contacts', 'contacts', contactsView);
route('/companies', 'companies', companiesView);
route('/activity', 'activity', activityView);
route('/settings', 'settings', settingsView);

// Labels are translated at render time (the locale is known after /auth/me).
const NAV = [
  { key: 'dashboard', href: '/', label: tk('Übersicht'), short: tk('Übersicht'), icon: '◎' },
  { key: 'projects', href: '/projects', label: tk('Projekte'), short: tk('Projekte'), icon: '▦' },
  { key: 'calendar', href: '/calendar', label: tk('Kalender'), short: tk('Kalender'), icon: '▣' },
  { key: 'contacts', href: '/contacts', label: tk('Kontakte'), short: tk('Kontakte'), icon: '◇' },
  { key: 'companies', href: '/companies', label: tk('Firmen'), short: tk('Firmen'), icon: '▤' },
  { key: 'activity', href: '/activity', label: tk('Verlauf'), short: tk('Verlauf'), icon: '≡' },
  { key: 'settings', href: '/settings', label: tk('Einstellungen'), short: tk('Setup'), icon: '⚙' },
];

// Guests only work inside shared projects.
const GUEST_NAV = new Set(['dashboard', 'projects', 'calendar', 'activity']);

const root = document.getElementById('app')!;

// ---------------------------------------------------------------- theme

function applyTheme(mode: 'light' | 'dark') {
  document.documentElement.dataset.pMode = mode;
  const toggle = document.querySelector<HTMLButtonElement>('[data-theme-toggle]');
  if (toggle) {
    toggle.setAttribute('aria-pressed', String(mode === 'dark'));
    toggle.textContent = mode === 'dark' ? '☾' : '☀';
  }
}

function initialTheme(): 'light' | 'dark' {
  try {
    const stored = localStorage.getItem('pma-theme');
    if (stored === 'light' || stored === 'dark') return stored;
  } catch {
    // Storage may be unavailable (private mode); fall back to the system preference.
  }
  return matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}
applyTheme(initialTheme());

// ---------------------------------------------------------------- shell

function renderShell(me: Me) {
  const org = me.organizations.find((o) => o.id === me.organizationId);
  mount(
    root,
    html`<a class="pma-skip-link" href="#view">${t('Zum Inhalt')}</a>
    <div class="p-app-shell pma-shell">
      <aside class="p-app-shell__sidebar pma-sidebar">
        <a class="pma-brand" href="/" aria-label="${brand.name}, ${t('Übersicht')}"><img class="pma-brand-logo" src="${brand.logo}" alt="" width="36" height="36">
          <span><strong>${brand.shortName}</strong><small>${brand.tagline}</small></span></a>
        <nav class="pma-nav" aria-label="${t('Hauptnavigation')}">${NAV.filter((item) => me.role !== 'guest' || GUEST_NAV.has(item.key)).map(
          (item) => html`<a class="pma-nav__link" href="${item.href}" data-nav="${item.key}"><span aria-hidden="true" class="pma-nav__icon">${item.icon}</span>${t(item.label)}</a>`,
        )}</nav>
        <div class="pma-sidebar__footer">
          ${
            me.organizations.length > 1
              ? html`<label class="p-field"><span class="p-label">${t('Organisation')}</span>
                  <select class="p-select" data-p-size="sm" data-org>${me.organizations.map(
                    (o) => html`<option value="${o.id}"${o.id === me.organizationId ? html` selected` : ''}>${o.name}</option>`,
                  )}</select></label>`
              : html`<p class="pma-small"><strong>${org?.name ?? ''}</strong><br><span class="pma-muted">${ROLE[me.role ?? ''] ?? ''}</span></p>`
          }
        </div>
      </aside>
      <header class="p-app-shell__header pma-header">
        <form class="pma-global-search" role="search" data-search-form>
          <label class="p-visually-hidden" for="pma-search">${t('Suchen')}</label>
          <input id="pma-search" class="p-input" type="search" placeholder="${t('Suchen … (Projekte, Aufgaben, Risiken, Kontakte)')}" autocomplete="off"
            aria-controls="pma-search-results" aria-expanded="false" data-search>
          <div id="pma-search-results" class="p-popover pma-search-results" role="listbox" hidden></div>
        </form>
        <div class="pma-header__actions">
          ${me.role !== 'guest' ? html`<a class="p-button p-button--ghost p-button--icon pma-mobile-only" href="/settings" data-nav="settings" aria-label="${t('Einstellungen')}">⚙</a>` : ''}
          <div class="pma-bell">
            <button type="button" class="p-button p-button--ghost p-button--icon" data-bell aria-haspopup="true" aria-expanded="false" aria-controls="pma-bell-panel" aria-label="${t('Benachrichtigungen')}">🔔<span class="pma-bell__count" data-bell-count hidden></span></button>
            <div id="pma-bell-panel" class="p-popover pma-bell__panel" role="region" aria-label="${t('Benachrichtigungen')}" hidden></div>
          </div>
          <button type="button" class="p-button p-button--ghost p-button--icon" data-theme-toggle aria-label="${t('Dunkles Design')}"></button>
          <a class="pma-user" href="/settings?tab=profile" title="${me.user.email} · ${t('Profil')}"><span class="p-avatar p-avatar--sm" aria-hidden="true">${me.user.name.slice(0, 1).toUpperCase()}</span>
            <span class="pma-user__name">${me.user.name}</span></a>
          <button type="button" class="p-button p-button--ghost p-button--sm" data-logout>${t('Abmelden')}</button>
        </div>
      </header>
      <main class="p-app-shell__main" id="view-main">
        ${releaseNotice('card')}
        <div class="p-app-shell__content pma-content" id="view" tabindex="-1"></div>
        ${siteFooter('pma-app-footer')}
      </main>
    </div>
    <nav class="p-bottom-nav pma-bottom-nav" data-p-placement="floating" aria-label="${t('Mobile Hauptnavigation')}"><ul>${NAV.filter(
      (item) => !['settings', 'companies'].includes(item.key) && (me.role !== 'guest' || GUEST_NAV.has(item.key)),
    ).map(
      (item) => html`<li><a href="${item.href}" data-nav="${item.key}"><span class="p-bottom-nav__icon" aria-hidden="true">${item.icon}</span><span class="p-bottom-nav__label">${t(item.short)}</span></a></li>`,
    )}</ul></nav>
    <div class="p-toast-region" data-p-toast-region aria-live="polite"></div>`,
  );
  applyTheme((document.documentElement.dataset.pMode as 'light' | 'dark') ?? 'dark');
  wireChrome(root);
}

// ---------------------------------------------------------------- notifications

const BELL_TAB: Record<string, string> = { task: 'tasks', risk: 'risks', decision: 'decisions', note: 'notes', gate: 'gates' };
let bellTimer: number | undefined;

async function refreshBell(renderPanel = false) {
  const button = document.querySelector<HTMLElement>('[data-bell]');
  if (!button || !state.me) return;
  try {
    const { items, unread } = await api.get<{ items: any[]; unread: number }>('/notifications?limit=20');
    const count = document.querySelector<HTMLElement>('[data-bell-count]')!;
    count.hidden = unread === 0;
    count.textContent = unread > 99 ? '99+' : String(unread);
    button.setAttribute('aria-label', unread ? t('Benachrichtigungen, {n} ungelesen', { n: unread }) : t('Benachrichtigungen'));
    if (!renderPanel) return;
    const panel = document.getElementById('pma-bell-panel')!;
    panel.innerHTML = html`<div class="pma-bell__head"><strong>${t('Benachrichtigungen')}</strong>
        ${unread ? html`<button type="button" class="p-button p-button--ghost p-button--sm" data-bell-read-all>${t('Alle gelesen')}</button>` : ''}</div>
      ${items.length ? html`<ul class="pma-bell__list">${items.map(
        (n) => html`<li><a class="pma-bell__item${n.read_at ? '' : ' pma-bell__item--unread'}" data-notification="${n.id}"
          href="/projects/${n.project_id}?tab=${BELL_TAB[n.entity_type] ?? 'overview'}">
          <span>${n.title}</span>${n.excerpt ? html`<span class="pma-muted pma-small">${n.excerpt}</span>` : ''}
          <span class="pma-muted pma-small">${formatDateTime(n.created_at)}</span></a></li>`,
      )}</ul>` : html`<p class="pma-muted pma-small">${t('Keine Benachrichtigungen. Erwähnungen, Zuweisungen und Kommentare zu deinen Aufgaben erscheinen hier.')}</p>`}`.value;
  } catch {
    // Keep the shell usable if the endpoint is unreachable.
  }
}

function startBell() {
  window.clearInterval(bellTimer);
  void refreshBell();
  bellTimer = window.setInterval(() => void refreshBell(!document.getElementById('pma-bell-panel')?.hidden), 60_000);
}

// ---------------------------------------------------------------- search

let searchTimer: number | undefined;
const SEARCH_TAB: Record<string, string> = { task: 'tasks', risk: 'risks', decision: 'decisions', note: 'notes' };

function searchHref(item: { type: string; project_id: string | null }) {
  if (item.type === 'contact') return '/contacts';
  if (item.type === 'company') return '/companies';
  const tab = SEARCH_TAB[item.type];
  return `/projects/${item.project_id}${tab ? `?tab=${tab}` : ''}`;
}

async function runSearch(input: HTMLInputElement) {
  const results = document.getElementById('pma-search-results')!;
  const q = input.value.trim();
  if (q.length < 2) {
    results.hidden = true;
    input.setAttribute('aria-expanded', 'false');
    return;
  }
  const { items } = await api.get(`/search?q=${encodeURIComponent(q)}`);
  results.innerHTML = items.length
    ? html`${items.map(
        (item: any) => html`<a class="pma-search-results__item" role="option" href="${searchHref(item)}"><span class="pma-muted pma-small">${ENTITY[item.type] ?? item.type}</span> ${item.label}</a>`,
      )}`.value
    : html`<p class="pma-muted pma-small">${t('Keine Treffer.')}</p>`.value;
  results.hidden = false;
  input.setAttribute('aria-expanded', 'true');
}

// ---------------------------------------------------------------- routing

let renderToken = 0;

async function renderRoute() {
  const view = document.getElementById('view');
  if (!view) return;
  const found = match(location.pathname);
  for (const link of document.querySelectorAll<HTMLElement>('[data-nav]')) {
    if (found && link.dataset.nav === found.route.nav) link.setAttribute('aria-current', 'page');
    else link.removeAttribute('aria-current');
  }
  const results = document.getElementById('pma-search-results');
  if (results) results.hidden = true;
  const token = ++renderToken;
  // Replace the view container so listeners of the previous view are dropped.
  const fresh = view.cloneNode(false) as HTMLElement;
  view.replaceWith(fresh);
  mount(fresh, loading());
  if (!found) {
    fresh.innerHTML = html`<h1>${t('Seite nicht gefunden')}</h1><p><a href="/">${t('Zur Übersicht')}</a></p>`.value;
    return;
  }
  try {
    await found.route.view(fresh, found.params, new URLSearchParams(location.search));
    if (token === renderToken) {
      const heading = fresh.querySelector('h1')?.textContent;
      document.title = `${heading ? `${heading} · ` : ''}${brand.name}`;
      fresh.focus({ preventScroll: true });
    }
  } catch (error) {
    if (token !== renderToken) return;
    fresh.innerHTML = '';
    toastError(error);
  }
}

const LEGAL = { '/impressum': 'impressum', '/datenschutz': 'datenschutz' } as const;

async function boot() {
  const [me] = await Promise.all([api.get<Me>('/auth/me').catch(() => null), loadSite()]);
  state.me = me;
  state.cache.clear();
  const legal = LEGAL[location.pathname as keyof typeof LEGAL];
  if (legal) {
    applyLocale(state.me?.user.locale ?? guestLocale());
    await renderLegal(root, legal);
    return;
  }
  const invite = location.pathname.match(/^\/invite\/(pmai_[A-Za-z0-9_-]+)$/);
  if (invite) {
    applyLocale(state.me?.user.locale ?? guestLocale());
    // Full reload afterwards: membership, organization and locale all change.
    await renderInvite(root, invite[1]!, () => location.assign('/'));
    return;
  }
  if (location.pathname === '/reset') {
    applyLocale(state.me?.user.locale ?? guestLocale());
    // The reset signs in again; reload so the new session drives the app.
    await renderReset(root, () => location.assign('/'));
    return;
  }
  if (!state.me) {
    applyLocale(guestLocale());
    const mode = location.pathname === '/signup' ? 'signup' : 'login';
    if (location.pathname !== '/login' && location.pathname !== '/signup') history.replaceState(null, '', '/login');
    renderAuth(root, mode, () => {
      // Only in-app invitation pages are accepted as a return target.
      const next = new URLSearchParams(location.search).get('next');
      history.replaceState(null, '', next && /^\/invite\/pmai_[A-Za-z0-9_-]+$/.test(next) ? next : '/');
      void boot();
    });
    return;
  }
  if (location.pathname === '/login' || location.pathname === '/signup') history.replaceState(null, '', '/');
  if (englishApplied() && state.me.user.locale !== 'en') {
    location.reload();
    return;
  }
  applyLocale(state.me.user.locale);
  rememberLocale(state.me.user.locale);
  renderShell(state.me);
  startBell();
  await renderRoute();
}

// Same-origin link interception for client-side navigation.
document.addEventListener('click', (event) => {
  if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  const anchor = (event.target as HTMLElement).closest('a');
  if (!anchor || anchor.target || anchor.hasAttribute('download')) return;
  const url = new URL(anchor.href, location.href);
  if (url.origin !== location.origin || url.pathname.startsWith('/api/')) return;
  if (url.hash && url.pathname === location.pathname && url.search === location.search) return;
  event.preventDefault();
  if ((!state.me && ['/login', '/signup', '/reset'].includes(url.pathname)) || url.pathname in LEGAL || location.pathname in LEGAL) {
    history.pushState(null, '', url);
    void boot();
    return;
  }
  navigate(url.pathname + url.search);
});

document.addEventListener('click', async (event) => {
  const element = event.target as HTMLElement;
  if (element.closest('[data-logout]')) {
    await api.post('/auth/logout').catch(() => {});
    state.me = null;
    history.replaceState(null, '', '/login');
    void boot();
  }
  if (element.closest('[data-theme-toggle]')) {
    const next = document.documentElement.dataset.pMode === 'dark' ? 'light' : 'dark';
    applyTheme(next);
    try {
      localStorage.setItem('pma-theme', next);
    } catch {
      // Ignore unavailable storage.
    }
  }
  const bell = element.closest<HTMLElement>('[data-bell]');
  const panel = document.getElementById('pma-bell-panel');
  if (bell && panel) {
    const open = panel.hidden;
    panel.hidden = !open;
    bell.setAttribute('aria-expanded', String(open));
    if (open) await refreshBell(true);
  } else if (panel && !panel.hidden && !element.closest('#pma-bell-panel')) {
    panel.hidden = true;
    document.querySelector('[data-bell]')?.setAttribute('aria-expanded', 'false');
  }
  if (element.closest('[data-bell-read-all]')) {
    await api.post('/notifications/read', {}).catch(() => {});
    await refreshBell(true);
  }
  const notificationId = element.closest<HTMLElement>('[data-notification]')?.dataset.notification;
  if (notificationId) {
    if (panel) panel.hidden = true;
    void api.post('/notifications/read', { ids: [notificationId] }).then(() => refreshBell()).catch(() => {});
  }
  if (!element.closest('[data-search-form]')) {
    const results = document.getElementById('pma-search-results');
    if (results) results.hidden = true;
  }
});

document.addEventListener('change', async (event) => {
  const element = event.target as HTMLSelectElement;
  if (element.matches('[data-org]')) {
    try {
      await api.post('/auth/organization', { organizationId: element.value });
      history.replaceState(null, '', '/');
      await boot();
    } catch (error) {
      toastError(error);
    }
  }
});

document.addEventListener('input', (event) => {
  const input = event.target as HTMLInputElement;
  if (!input.matches('[data-search]')) return;
  clearTimeout(searchTimer);
  searchTimer = window.setTimeout(() => void runSearch(input).catch(toastError), 200);
});

document.addEventListener('submit', (event) => {
  if ((event.target as HTMLElement).matches('[data-search-form]')) {
    event.preventDefault();
    document.querySelector<HTMLElement>('.pma-search-results__item')?.click();
  }
});

document.addEventListener('keydown', (event) => {
  if (event.key === '/' && !(event.target as HTMLElement).closest('input, textarea, select, [contenteditable]')) {
    event.preventDefault();
    document.querySelector<HTMLInputElement>('[data-search]')?.focus();
  }
  if (event.key === 'Escape') {
    const results = document.getElementById('pma-search-results');
    if (results && !results.hidden) results.hidden = true;
  }
});

// Back/forward between the app and a standalone page (legal, sign-in) re-boots.
window.addEventListener('popstate', () => void (document.querySelector('.pma-shell') && !(location.pathname in LEGAL) ? renderRoute() : boot()));
window.addEventListener('pma:navigate', () => void renderRoute());
window.addEventListener('pma:unauthenticated', () => void boot());

void boot();
